import { useState, useEffect, useCallback } from "react";
import { AWARDS_API, fetchWithAuth } from "../utils/api";
import { scoringRulesService } from "../services/scoringRulesService";

// Default judge station settings
const DEFAULT_JUDGE_STATION_SETTINGS = {
  livescoreMonitorEnabled: false, // Default to false to respect admin settings
  allowJudgeNavigation: true,
  showOtherJudgeScores: true,
  enableScoreComments: true,
};

export function useJudgeStationSettings() {
  const [judgeStationSettings, setJudgeStationSettings] = useState(DEFAULT_JUDGE_STATION_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isExistingConfig, setIsExistingConfig] = useState(false);

  /**
   * Fetch judge station settings from AWS
   */
  const fetchJudgeStationSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch from the same AWARDS_API endpoint that stores all judging-related settings
      const data = await fetchWithAuth(AWARDS_API);
      
      console.log('Fetched data from AWARDS_API:', data);
      console.log('Judge station settings from API:', data.judgeStationSettings);
      
      // Check if we have existing judge station settings
      const hasExistingData = data.judgeStationSettings && Object.keys(data.judgeStationSettings).length > 0;
      console.log('Has existing judge station data:', hasExistingData);
      
      setIsExistingConfig(hasExistingData);
      
      // Merge with defaults to ensure all properties exist
      const settings = {
        ...DEFAULT_JUDGE_STATION_SETTINGS,
        ...(data.judgeStationSettings || {})
      };
      
      console.log('Final merged settings:', settings);
      
      setJudgeStationSettings(settings);
      setHasChanges(false);
    } catch (err) {
      setError(err.message || "Failed to load judge station settings");
      console.error("Error loading judge station settings:", err);
      // Use defaults on error
      setJudgeStationSettings(DEFAULT_JUDGE_STATION_SETTINGS);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Save judge station settings to AWS
   */
  const saveJudgeStationSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      // First, get the current configuration to preserve other settings
      const currentConfig = await fetchWithAuth(AWARDS_API);
      
      // Check if there's an existing configuration by looking at the current data
      // This is more reliable than relying on the state variable
      const hasExistingConfig = currentConfig && (
        currentConfig.awards?.length > 0 || 
        currentConfig.criteria?.length > 0 || 
        currentConfig.scoringRules || 
        currentConfig.judgeStationSettings
      );
      
      // Use POST for new configurations, PUT for updates
      const method = hasExistingConfig ? "PUT" : "POST";
      const actionText = hasExistingConfig ? "updated" : "created";
      
      console.log(`Using ${method} method for judge station settings (existing: ${hasExistingConfig})`);
      console.log('Current config:', currentConfig);
      console.log('Judge station settings to save:', judgeStationSettings);
      
      // Merge judge station settings with existing configuration
      const updatedConfig = {
        ...currentConfig,
        judgeStationSettings
      };
      
      console.log('Saving updated config:', updatedConfig);
      console.log('Updated config judgeStationSettings:', updatedConfig.judgeStationSettings);
      
      const response = await fetchWithAuth(AWARDS_API, {
        method: method,
        body: JSON.stringify(updatedConfig)
      });
      
      console.log('Save response:', response);
      console.log('Save response judgeStationSettings:', response?.judgeStationSettings);
      console.log('Full save response keys:', Object.keys(response || {}));
      
      // Update local state with the saved data
      if (response && response.judgeStationSettings) {
        console.log('Updating local settings with response data:', response.judgeStationSettings);
        setJudgeStationSettings(response.judgeStationSettings);
      } else {
        console.log('No judgeStationSettings in response, keeping current settings');
      }
      
      // Mark as existing config now that we've saved
      setIsExistingConfig(true);
      setHasChanges(false);
      setSuccessMessage(`Judge station settings ${actionText} successfully!`);
      
      // Clear scoring rules cache so competitions get updated settings
      scoringRulesService.clearCache();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
      
      return true;
    } catch (err) {
      setError(err.message || "Failed to save judge station settings");
      console.error("Error saving judge station settings:", err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [judgeStationSettings]);

  /**
   * Update judge station settings and mark as changed
   */
  const updateJudgeStationSettings = useCallback((settings) => {
    setJudgeStationSettings(settings);
    setHasChanges(true);
    setError(null);
    setSuccessMessage(null);
  }, []);

  /**
   * Update a single setting
   */
  const updateSetting = useCallback((key, value) => {
    setJudgeStationSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
    setError(null);
    setSuccessMessage(null);
  }, []);

  /**
   * Reset settings to last saved state
   */
  const resetSettings = useCallback(() => {
    fetchJudgeStationSettings();
  }, [fetchJudgeStationSettings]);

  // Load settings on mount
  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchJudgeStationSettingsWithAbort = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch from the same AWARDS_API endpoint that stores all judging-related settings
        const data = await fetchWithAuth(AWARDS_API, { signal: abortController.signal });
        
        // Only update state if the request wasn't aborted
        if (!abortController.signal.aborted) {
          console.log('Fetched data from AWARDS_API:', data);
          console.log('Judge station settings from API:', data.judgeStationSettings);
          
          // Check if we have existing judge station settings
          const hasExistingData = data.judgeStationSettings && Object.keys(data.judgeStationSettings).length > 0;
          console.log('Has existing judge station data:', hasExistingData);
          
          setIsExistingConfig(hasExistingData);
          
          // Merge with defaults to ensure all properties exist
          const settings = {
            ...DEFAULT_JUDGE_STATION_SETTINGS,
            ...(data.judgeStationSettings || {})
          };
          
          console.log('Final merged settings:', settings);
          
          setJudgeStationSettings(settings);
          setHasChanges(false);
        }
      } catch (err) {
        // Only set error if the request wasn't aborted
        if (!abortController.signal.aborted) {
          setError(err.message || "Failed to load judge station settings");
          console.error("Error loading judge station settings:", err);
          // Use defaults on error
          setJudgeStationSettings(DEFAULT_JUDGE_STATION_SETTINGS);
          setIsExistingConfig(false);
        }
      } finally {
        // Only update loading state if the request wasn't aborted
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };
    
    fetchJudgeStationSettingsWithAbort();
    
    // Cleanup function to abort the request if component unmounts or effect re-runs
    return () => {
      abortController.abort();
    };
  }, []); // Remove fetchJudgeStationSettings dependency to prevent unnecessary re-runs

  return {
    // Settings state
    settings: judgeStationSettings,
    judgeStationSettings, // For backward compatibility
    
    // Loading and error states
    loading,
    error,
    hasChanges,
    successMessage,
    isExistingConfig,
    
    // Actions
    fetchJudgeStationSettings,
    saveJudgeStationSettings,
    updateJudgeStationSettings,
    updateSetting,
    resetSettings,
    
    // Convenience getters for commonly used settings
    isLivescoreMonitorEnabled: judgeStationSettings.livescoreMonitorEnabled,
    canShowOtherJudgeScores: judgeStationSettings.livescoreMonitorEnabled && judgeStationSettings.showOtherJudgeScores,
    isJudgeNavigationAllowed: judgeStationSettings.allowJudgeNavigation,
    areScoreCommentsEnabled: judgeStationSettings.enableScoreComments
  };
}
